import { createFileRoute, Link } from "@tanstack/react-router";
import { Typography, Table, Tag, Flex, Button, Input, Select, App, Form, Modal, theme } from "antd";
import { ArrowLeftOutlined, SendOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useQ_PageGodMode_Organizations } from "@/hooks/GodMode/useQ_PageGodMode_Organizations";
import { useM_PageGodMode_OrganizationUpdate } from "@/hooks/GodMode/useM_PageGodMode_OrganizationUpdate";
import { useQ_PageGodMode_$OrgId_Members } from "@/hooks/GodMode/useQ_PageGodMode_$OrgId_Members";
import { useM_PageGodMode_$OrgId_InviteUser } from "@/hooks/GodMode/useM_PageGodMode_$OrgId_InviteUser";
import { useQ_PageGodMode_Flows } from "@/hooks/GodMode/useQ_PageGodMode_Flows";
import { useQ_PageGodMode_$OrgId_Flows } from "@/hooks/GodMode/useQ_PageGodMode_$OrgId_Flows";
import { useM_PageGodMode_$OrgId_FlowGrant } from "@/hooks/GodMode/useM_PageGodMode_$OrgId_FlowGrant";
import { useM_PageGodMode_$OrgId_FlowRevoke } from "@/hooks/GodMode/useM_PageGodMode_$OrgId_FlowRevoke";
import { const_OrganizationsStatusOptions } from "@/hooks/GodMode/const_OrganizationsStatusOptions";
import { const_OrganizationMembersStatusOptions } from "@/hooks/GodMode/const_OrganizationMembersStatusOptions";
import { const_FlowStatusOptions } from "@/hooks/GodMode/const_FlowStatusOptions";
import type { Supabase_Enums } from "@gotoIHUB/db-types";
import type { PageGodMode_OrgId_Members_QueryData } from "@/hooks/GodMode/useQ_PageGodMode_$OrgId_Members";
import type { PageGodMode_OrgId_Flows_QueryData } from "@/hooks/GodMode/useQ_PageGodMode_$OrgId_Flows";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_protected/_god-mode/god-mode/$orgId")({
  component: Page_GodMode_OrgDetail,
});

function Page_GodMode_OrgDetail() {
  const { token } = theme.useToken();
  const { orgId } = Route.useParams();
  const { message } = App.useApp();
  const qOrganizations = useQ_PageGodMode_Organizations();
  const qMembers = useQ_PageGodMode_$OrgId_Members({ orgId });
  const mInviteUser = useM_PageGodMode_$OrgId_InviteUser({ orgId });
  const qAllFlows = useQ_PageGodMode_Flows();
  const qOrgFlows = useQ_PageGodMode_$OrgId_Flows({ orgId });
  const mFlowGrant = useM_PageGodMode_$OrgId_FlowGrant({ orgId });
  const mFlowRevoke = useM_PageGodMode_$OrgId_FlowRevoke({ orgId });
  const mOrgUpdate = useM_PageGodMode_OrganizationUpdate({ orgId });
  const [inviteEmail, setInviteEmail] = useState("");
  const [selectedFlowId, setSelectedFlowId] = useState<string | undefined>(undefined);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();

  const org = useMemo(
    () => qOrganizations.organizations.find((o) => o.id === orgId),
    [qOrganizations.organizations, orgId],
  );

  const grantedFlowIds = useMemo(
    () => new Set(qOrgFlows.orgFlows.map((of) => of.flow_id)),
    [qOrgFlows.orgFlows],
  );

  const ungrantedFlows = useMemo(
    () => qAllFlows.flows.filter((f) => !grantedFlowIds.has(f.id)),
    [qAllFlows.flows, grantedFlowIds],
  );

  const handleGrantFlow = async () => {
    if (!selectedFlowId) return;
    try {
      await mFlowGrant.mutation.mutateAsync({ flowId: selectedFlowId });
      message.success("Flow access granted");
      setSelectedFlowId(undefined);
    } catch {
      message.error("Failed to grant flow access");
    }
  };

  const handleRevokeFlow = async (flowId: string) => {
    try {
      await mFlowRevoke.mutation.mutateAsync({ flowId });
      message.success("Flow access revoked");
    } catch {
      message.error("Failed to revoke flow access");
    }
  };

  const flowColumns = [
    {
      title: "Slug",
      key: "slug",
      render: (_: unknown, record: PageGodMode_OrgId_Flows_QueryData[number]) =>
        <Typography.Text code>{(record.flows as { slug: string })?.slug}</Typography.Text>,
    },
    {
      title: "Name",
      key: "name",
      render: (_: unknown, record: PageGodMode_OrgId_Flows_QueryData[number]) =>
        (record.flows as { name: string })?.name,
    },
    {
      title: "Status",
      key: "status",
      render: (_: unknown, record: PageGodMode_OrgId_Flows_QueryData[number]) => {
        const status = (record.flows as { status: Supabase_Enums<"flow_status_enum"> })?.status;
        if (!status) return null;
        const meta = const_FlowStatusOptions.map[status];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : null;
      },
    },
    {
      title: "Granted",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: PageGodMode_OrgId_Flows_QueryData[number]) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => handleRevokeFlow(record.flow_id)}
          loading={mFlowRevoke.mutation.isPending}
        />
      ),
    },
  ];

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await mInviteUser.mutation.mutateAsync({ email: inviteEmail.trim() });
      message.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
    } catch (error) {
      message.error((error as Error).message || "Failed to send invitation");
    }
  };

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: Supabase_Enums<"organization_members_status_enum">) => {
        const meta = const_OrganizationMembersStatusOptions.map[status];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : null;
      },
    },
    {
      title: "Joined",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  const statusMeta = org ? const_OrganizationsStatusOptions.map[org.status] : null;

  return (
    <div>
      <Link to="/god-mode">
        <Button type="text" icon={<ArrowLeftOutlined />} style={{ marginBottom: token.marginMD }}>
          Back to Organizations
        </Button>
      </Link>

      <Flex align="center" gap={token.marginMD} style={{ marginBottom: token.marginSM }}>
        <Typography.Title level={2} style={{ margin: 0 }}>{org?.name ?? "Organization"}</Typography.Title>
        {statusMeta && <Tag color={statusMeta.color}>{statusMeta.label}</Tag>}
        <Button
          type="text"
          icon={<EditOutlined />}
          size="small"
          onClick={() => { setEditModalOpen(true); editForm.setFieldsValue({ name: org?.name, status: org?.status }); }}
        />
      </Flex>

      <Typography.Text type="secondary" copyable style={{ display: "block", marginBottom: token.marginLG }}>
        ID: {orgId}
      </Typography.Text>

      <Modal
        title="Edit Organization"
        open={editModalOpen}
        onCancel={() => { setEditModalOpen(false); editForm.resetFields(); }}
        onOk={() => editForm.submit()}
        confirmLoading={mOrgUpdate.mutation.isPending}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" onFinish={async (values: { name: string; status: Supabase_Enums<"organization_status"> }) => {
          try {
            await mOrgUpdate.mutation.mutateAsync(values);
            message.success("Organization updated");
            setEditModalOpen(false);
          } catch {
            message.error("Failed to update organization");
          }
        }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name is required" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select options={const_OrganizationsStatusOptions.options.map((o) => ({ value: o.value, label: o.label }))} />
          </Form.Item>
        </Form>
      </Modal>

      <Flex gap={token.marginSM} style={{ marginBottom: token.marginLG }}>
        <Input
          placeholder="Email address"
          type="email"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          onPressEnter={handleInvite}
          style={{ maxWidth: 320 }}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleInvite}
          loading={mInviteUser.mutation.isPending}
          disabled={!inviteEmail.trim()}
        >
          Invite
        </Button>
      </Flex>

      <Typography.Title level={4}>Members ({qMembers.members.length})</Typography.Title>
      <Table<PageGodMode_OrgId_Members_QueryData[number]>
        dataSource={qMembers.members}
        columns={columns}
        rowKey="id"
        loading={qMembers.query.isLoading}
        pagination={false}
        locale={{ emptyText: "No members yet. Invite a user by email above." }}
      />

      <Typography.Title level={4} style={{ marginTop: token.marginXL }}>
        Flow Access ({qOrgFlows.orgFlows.length})
      </Typography.Title>

      <Flex gap={token.marginSM} style={{ marginBottom: token.marginLG }}>
        <Select
          placeholder="Select flow to grant"
          value={selectedFlowId}
          onChange={setSelectedFlowId}
          style={{ minWidth: 240 }}
          options={ungrantedFlows.map((f) => ({ value: f.id, label: `${f.slug} — ${f.name}` }))}
          notFoundContent="All flows already granted"
        />
        <Button
          type="primary"
          onClick={handleGrantFlow}
          loading={mFlowGrant.mutation.isPending}
          disabled={!selectedFlowId}
        >
          Grant Access
        </Button>
      </Flex>

      <Table<PageGodMode_OrgId_Flows_QueryData[number]>
        dataSource={qOrgFlows.orgFlows}
        columns={flowColumns}
        rowKey="flow_id"
        loading={qOrgFlows.query.isLoading}
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: "No flows granted. Use the dropdown above to grant access." }}
      />
    </div>
  );
}
